import * as React from "react"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

const CardContext = React.createContext({
    variant: "default",
})

const useCardContext = () => {
    const context = React.useContext(CardContext)
    if (!context) {
        throw new Error("useCardContext must be used within a Card component")
    }
    return context
}

const cardVariants = cva("flex flex-col items-stretch text-card-foreground rounded-xl", {
    variants: {
        variant: {
            default: "bg-card border border-border shadow-xs black/5",
            accent: "bg-muted shadow-xs p-1",
        },
    },
    defaultVariants: {
        variant: "default",
    },
})

const cardHeaderVariants = cva("flex items-center justify-between flex-wrap px-5 min-h-14 gap-2.5", {
    variants: {
        variant: {
            default: "border-b border-border",
            accent: "",
        },
    },
    defaultVariants: {
        variant: "default",
    },
})

const cardContentVariants = cva("grow p-5", {
    variants: {
        variant: {
            default: "",
            accent: "bg-card rounded-t-xl [&:last-child]:rounded-b-xl",
        },
    },
    defaultVariants: {
        variant: "default",
    },
})

const cardFooterVariants = cva("flex items-center px-5 min-h-14", {
    variants: {
        variant: {
            default: "border-t border-border",
            accent: "bg-card rounded-b-xl mt-[2px]",
        },
    },
    defaultVariants: {
        variant: "default",
    },
})

const Card = React.forwardRef(({ className, variant = "default", ...props }, ref) => (
    <CardContext.Provider value={{ variant: variant || "default" }}>
        <div
            ref={ref}
            data-slot="card"
            className={cn(cardVariants({ variant }), className)}
            {...props}
        />
    </CardContext.Provider>
))
Card.displayName = "Card"

const CardHeader = React.forwardRef(({ className, ...props }, ref) => {
    const { variant } = useCardContext()
    return (
        <div
            ref={ref}
            data-slot="card-header"
            className={cn(cardHeaderVariants({ variant }), className)}
            {...props}
        />
    )
})
CardHeader.displayName = "CardHeader"

const CardContent = React.forwardRef(({ className, ...props }, ref) => {
    const { variant } = useCardContext()
    return (
        <div
            ref={ref}
            data-slot="card-content"
            className={cn(cardContentVariants({ variant }), className)}
            {...props}
        />
    )
})
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef(({ className, ...props }, ref) => {
    const { variant } = useCardContext()
    return (
        <div
            ref={ref}
            data-slot="card-footer"
            className={cn(cardFooterVariants({ variant }), className)}
            {...props}
        />
    )
})
CardFooter.displayName = "CardFooter"

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
    <h3
        ref={ref}
        data-slot="card-title"
        className={cn("text-base font-semibold leading-none tracking-tight", className)}
        {...props}
    />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
    <div
        ref={ref}
        data-slot="card-description"
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
))
CardDescription.displayName = "CardDescription"

export { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription }
